import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { SkipLink } from "./components/a11y";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import MagicLinkCallback from "./pages/MagicLinkCallback";
import AlreadyLoggedIn from "./pages/AlreadyLoggedIn";
import ChangePasswordOtp from "./pages/ChangePasswordOtp";
import SidebarLayout from "./components/SidebarLayout";
import RequirePasswordChangeClear from "./components/RequirePasswordChangeClear";
import RequirePermission, {
  RequireAnyPermission,
} from "./components/RequirePermission";
import { usePortalPermissions } from "./hooks/usePortalPermissions";
import { ConnectionBanner } from "./components/common";

import { getCurrentUser, homePathForRole, type Role } from "./services/auth";
import { installPageTracker } from "./services/audit";
import { initConnectionMonitor } from "./services/connection";
import { Suspense, lazy, useEffect } from "react";
import { PageSkeleton } from "./components/ui";

// Help system components
import {
  ErrorBoundary,
  CommandPalette,
  HelpProvider,
  OnboardingTour,
} from "./components/help";

/* ── Lazy-loaded pages for parallel code-splitting ── */
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateProject = lazy(() => import("./pages/CreateProject"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ProjectEmployees = lazy(() => import("./pages/ProjectEmployees"));
const ProjectTasks = lazy(() => import("./pages/ProjectTasks"));
const CreateTask = lazy(() => import("./pages/CreateTask"));
const AdminBugs = lazy(() => import("./pages/AdminBugs"));
const AdminBugDetail = lazy(() => import("./pages/AdminBugDetail"));
const ExternalBugs = lazy(() => import("./pages/ExternalBugs"));
const TaskDetail = lazy(() => import("./pages/TaskDetail"));
const EditTask = lazy(() => import("./pages/EditTask"));
const ManagerProjects = lazy(() => import("./pages/ManagerProjects"));
const ManagerTasks = lazy(() => import("./pages/ManagerTasks"));
const PostTask = lazy(() => import("./pages/PostTask"));
const CreateLeave = lazy(() => import("./pages/CreateLeave"));
const EmployeeLeaves = lazy(() => import("./pages/EmployeeLeaves"));
const PendingLeaves = lazy(() => import("./pages/PendingLeaves"));
const LeaveDetail = lazy(() => import("./pages/LeaveDetail"));
const ApproveLeave = lazy(() => import("./pages/ApproveLeave"));
const RejectLeave = lazy(() => import("./pages/RejectLeave"));
const PostWorkLog = lazy(() => import("./pages/PostWorkLog"));
const EmployeeWorkLogs = lazy(() => import("./pages/EmployeeWorkLogs"));
const PendingWorkLogs = lazy(() => import("./pages/PendingWorkLogs"));
const WorkLogDetail = lazy(() => import("./pages/WorkLogDetail"));
const WorkLogReview = lazy(() => import("./pages/WorkLogReview"));
const EmployeeTasks = lazy(() => import("./pages/EmployeeTasks"));
const EmployeeProjects = lazy(() => import("./pages/EmployeeProjects"));
const TimeLogsPage = lazy(() => import("./pages/TimeLogsPage"));
const EmployeeIssues = lazy(() => import("./pages/EmployeeIssues"));
const ManagerIssues = lazy(() => import("./pages/ManagerIssues"));
const WeeklyLog = lazy(() => import("./pages/WeeklyLog"));
const Timesheets = lazy(() => import("./pages/Timesheets"));
const TimesheetDetail = lazy(() => import("./pages/TimesheetDetail"));
const AdminAssignManager = lazy(() => import("./pages/AdminAssignManager"));
const AdminAssignEmployee = lazy(() => import("./pages/AdminAssignEmployee"));
const AdminPeople = lazy(() => import("./pages/AdminPeople"));
const AdminAddEmployee = lazy(() => import("./pages/AdminAddEmployee"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const EmployeeDetail = lazy(() => import("./pages/EmployeeDetail"));
const AdminAllWorkLogs = lazy(() => import("./pages/AdminAllWorkLogs"));
const ProjectManagers = lazy(() => import("./pages/ProjectManagers"));
const TimesheetImportPage = lazy(() => import("./pages/TimesheetImportPage"));
const Invoices = lazy(() => import("./pages/Invoices"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const Settings = lazy(() => import("./pages/Settings"));
const ProjectView = lazy(() => import("./pages/ProjectView"));
const IssueDetail = lazy(() => import("./pages/IssueDetail"));
const ProjectSetup = lazy(() => import("./pages/ProjectSetup"));
const PortalSetup = lazy(() => import("./pages/PortalSetup"));
const AdminMigrations = lazy(() => import("./pages/AdminMigrations"));
const Leaves = lazy(() => import("./pages/Leaves"));

const BulkTaskUpload = lazy(() => import("./pages/BulkTaskUpload"));
const BulkProjectUpload = lazy(() => import("./pages/BulkProjectUpload"));
const BulkUserUpload = lazy(() => import("./pages/BulkUserUpload"));
const AdminAuditLogs = lazy(() => import("./pages/AdminAuditLogs"));
const WorkLogCalendarPage = lazy(() => import("./pages/WorkLogCalendarPage"));
const AdminImportExport = lazy(() => import("./pages/AdminImportExport"));
const BulkWorkLogUpload = lazy(() => import("./pages/BulkWorkLogUpload"));
const ManagerImportExport = lazy(() => import("./pages/ManagerImportExport"));
const AdminAllocations = lazy(() => import("./pages/AdminAllocations"));
const Allocations = lazy(() => import("./pages/Allocations"));

interface GuardProps {
  children: React.ReactElement;
}

function RequireAuth({ children }: GuardProps) {
  const user = getCurrentUser();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return children;
}

function RequireRoles({ roles, children }: GuardProps & { roles: Role[] }) {
  const user = getCurrentUser();
  const location = useLocation();
  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  if (!roles.includes(user.role)) {
    return <Navigate to={homePathForRole(user.role, user.id)} replace />;
  }
  return children;
}

/**
 * RequirePortalAccess — guards a route by portal module permission (can_read).
 * This is now a wrapper around RequirePermission for backward compatibility.
 * Unlike RequireRoles, this respects the Admin Setup configuration so that
 * any role (e.g. PROJECT_MANAGER) that has been granted access to a module
 * can navigate to it without being bounced back to the dashboard.
 *
 * Falls back to RequireRoles behaviour only if the user is not authenticated.
 */
function RequirePortalAccess({
  moduleCode,
  action = "read",
  children,
}: GuardProps & {
  moduleCode: string;
  action?:
    | "create"
    | "read"
    | "update"
    | "delete"
    | "approve"
    | "import"
    | "export";
}) {
  const user = getCurrentUser();
  const location = useLocation();
  const { canDo, loading } = usePortalPermissions();

  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // While permissions are loading, render nothing (avoids flash-redirect).
  // The portal permissions hook initialises from localStorage synchronously
  // on mount, so this is typically < 1 frame.
  if (loading) return null;

  if (!canDo(moduleCode, action)) {
    return <Navigate to={homePathForRole(user.role, user.id)} replace />;
  }

  return children;
}

/* Pick the correct task detail page based on the logged-in user's role.
 * Admin/manager keep the original full-control page; employees get the
 * employee-specific view.
 */
function RoleBasedTaskDetail() {
  return <TaskDetail />;
}

/* For employee-scoped URLs like /employees/:employeeId/tasks.
 * Employees may only view their own; managers/admins may view any.
 * If an employee lands on someone else's URL, rewrite the param to their id.
 */
function RequireSelfOrManager({
  paramName,
  children,
}: GuardProps & { paramName: string }) {
  const user = getCurrentUser();
  const params = useParams();
  const location = useLocation();

  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  const target = params[paramName];
  if (
    user.role === "EMPLOYEE" &&
    target &&
    String(user.id) !== String(target)
  ) {
    const fixed = location.pathname.replace(`/${target}`, `/${user.id}`);
    return <Navigate to={fixed} replace />;
  }

  return children;
}

function App() {
  useEffect(() => {
    // Initialize connection monitoring first
    initConnectionMonitor();
    // Then install page tracker (which now checks connection state)
    installPageTracker();
  }, []);
  return (
    <ErrorBoundary>
      <ConnectionBanner />
      <SkipLink />
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          {/* Login route — no sidebar */}
          <Route path="/" element={<Login />} />

          {/* Public auth routes — no sidebar */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/magic-link" element={<MagicLinkCallback />} />
          <Route path="/already-logged-in" element={<AlreadyLoggedIn />} />

          {/* Change password — any authenticated user. Rendered WITHOUT
           * the SidebarLayout and WITHOUT RequirePasswordChangeClear
           * (otherwise the guard would loop forever redirecting here to
           * here). RequireAuth ensures a valid JWT is present; the
           * status peek inside the page itself drops the user back to
           * their dashboard if the lock has already been cleared. */}
          <Route
            path="/change-password"
            element={
              <RequireAuth>
                <ChangePasswordOtp />
              </RequireAuth>
            }
          />

          {/* Settings pages — rendered WITHOUT the main sidebar */}
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <RequirePasswordChangeClear>
                  <Settings />
                </RequirePasswordChangeClear>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <RequirePortalAccess moduleCode="SETTINGS">
                <RequirePasswordChangeClear>
                  <AdminSettings />
                </RequirePasswordChangeClear>
              </RequirePortalAccess>
            }
          />

          {/* All other routes share the SidebarLayout */}
          <Route element={<SidebarLayout />}>
            {/* Unified dashboard route */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <Dashboard />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />

            {/* Legacy dashboard routes — redirect to /dashboard */}
            <Route
              path="/admin"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/admin/dashboard"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/manager"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/manager/dashboard"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/employee"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/employee/dashboard"
              element={<Navigate to="/dashboard" replace />}
            />

            {/* Projects — unified for all roles */}
            <Route
              path="/projects"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <EmployeeProjects />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectDetail />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:id/employees"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectEmployees />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:id/managers"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectManagers />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            {/* Project tabbed view (role-based tabs) */}
            <Route
              path="/projects/:projectId/view"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectView />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            {/* Issue detail page */}
            <Route
              path="/projects/:projectId/issues/:issueId"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <IssueDetail />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            {/* Project Setup — role & permission management */}
            <Route
              path="/projects/:projectId/setup"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectSetup />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            {/* Portal Setup — admin portal role management */}
            <Route
              path="/admin/setup"
              element={
                <RequirePortalAccess moduleCode="SETUP">
                  <RequirePasswordChangeClear>
                    <PortalSetup />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            {/* Migrations */}
            <Route
              path="/admin/migrations"
              element={
                <RequirePortalAccess moduleCode="MIGRATIONS">
                  <RequirePasswordChangeClear>
                    <AdminMigrations />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            {/* Leaves — common for all roles */}
            <Route
              path="/leaves"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <Leaves />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:id/tasks"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <ProjectTasks />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/projects/:projectId/tasks/new"
              element={
                <RequirePortalAccess moduleCode="TASK" action="create">
                  <RequirePasswordChangeClear>
                    <CreateTask />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/projects/:projectId/tasks/bulk-upload"
              element={
                <RequirePortalAccess moduleCode="TASK" action="import">
                  <RequirePasswordChangeClear>
                    <BulkTaskUpload />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/tasks/import "
              element={
                <RequirePortalAccess moduleCode="TASK" action="import">
                  <RequirePasswordChangeClear>
                    <BulkTaskUpload />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/createproject"
              element={
                <RequirePortalAccess moduleCode="PROJECT" action="create">
                  <RequirePasswordChangeClear>
                    <CreateProject />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/projects/bulk-upload"
              element={
                <RequirePortalAccess moduleCode="PROJECT" action="import">
                  <RequirePasswordChangeClear>
                    <BulkProjectUpload />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/users/bulk-upload"
              element={
                <RequirePortalAccess moduleCode="USER" action="import">
                  <RequirePasswordChangeClear>
                    <BulkUserUpload />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <RequirePortalAccess moduleCode="AUDIT_LOG">
                  <RequirePasswordChangeClear>
                    <AdminAuditLogs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/bugs"
              element={
                <RequirePortalAccess moduleCode="BUG">
                  <RequirePasswordChangeClear>
                    <AdminBugs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/bugs/:bugId"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <AdminBugDetail />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/admin/external-bugs"
              element={
                <RequirePortalAccess moduleCode="BUG">
                  <RequirePasswordChangeClear>
                    <ExternalBugs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/managers/:managerId/projects"
              element={
                <RequirePortalAccess moduleCode="PROJECTS">
                  <RequirePasswordChangeClear>
                    <ManagerProjects />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/managers/:managerId/post-task"
              element={
                <RequirePortalAccess moduleCode="TASK" action="create">
                  <RequirePasswordChangeClear>
                    <PostTask />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/manager/tasks"
              element={
                <RequirePortalAccess moduleCode="TASK">
                  <RequirePasswordChangeClear>
                    <ManagerTasks />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />

            {/* Tasks */}
            <Route
              path="/tasks/:taskId"
              element={
                <RequireAuth>
                  <RequirePasswordChangeClear>
                    <RoleBasedTaskDetail />
                  </RequirePasswordChangeClear>
                </RequireAuth>
              }
            />
            <Route
              path="/tasks/:taskId/edit"
              element={
                <RequirePortalAccess moduleCode="TASK" action="update">
                  <RequirePasswordChangeClear>
                    <EditTask />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />

            {/* Leaves (static paths first, then dynamic) */}
            <Route
              path="/leaves/new"
              element={
                <RequirePortalAccess moduleCode="LEAVE" action="create">
                  <RequirePasswordChangeClear>
                    <CreateLeave />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/leaves/pending"
              element={
                <RequirePortalAccess moduleCode="LEAVE" action="approve">
                  <RequirePasswordChangeClear>
                    <PendingLeaves />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/leaves/:leaveId/approve"
              element={
                <RequirePortalAccess moduleCode="LEAVE" action="approve">
                  <RequirePasswordChangeClear>
                    <ApproveLeave />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/leaves/:leaveId/reject"
              element={
                <RequirePortalAccess moduleCode="LEAVE" action="approve">
                  <RequirePasswordChangeClear>
                    <RejectLeave />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/leaves/:leaveId/edit"
              element={
                <RequirePortalAccess moduleCode="LEAVE" action="update">
                  <RequirePasswordChangeClear>
                    <CreateLeave />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/leaves/:leaveId"
              element={
                <RequirePortalAccess moduleCode="LEAVE">
                  <RequirePasswordChangeClear>
                    <LeaveDetail />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/employees/:employeeId/leaves"
              element={
                <RequirePortalAccess moduleCode="LEAVE">
                  <RequireSelfOrManager paramName="employeeId">
                    <EmployeeLeaves />
                  </RequireSelfOrManager>
                </RequirePortalAccess>
              }
            />

            {/* Work logs (static paths first, then dynamic) */}
            <Route
              path="/worklogs/new"
              element={
                <RequirePortalAccess moduleCode="WORKLOG" action="create">
                  <RequirePasswordChangeClear>
                    <PostWorkLog />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/worklogs/pending"
              element={
                <RequirePortalAccess moduleCode="WORKLOG" action="approve">
                  <RequirePasswordChangeClear>
                    <PendingWorkLogs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/worklogs/:workLogId/approve"
              element={
                <RequirePortalAccess moduleCode="WORKLOG" action="approve">
                  <RequirePasswordChangeClear>
                    <WorkLogReview />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/worklogs/:workLogId/reject"
              element={
                <RequirePortalAccess moduleCode="WORKLOG" action="approve">
                  <RequirePasswordChangeClear>
                    <WorkLogReview />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/worklogs/:workLogId"
              element={
                <RequirePortalAccess moduleCode="WORKLOG">
                  <RequirePasswordChangeClear>
                    <WorkLogDetail />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/employees/:employeeId/worklogs"
              element={
                <RequirePortalAccess moduleCode="WORKLOG">
                  <RequireSelfOrManager paramName="employeeId">
                    <EmployeeWorkLogs />
                  </RequireSelfOrManager>
                </RequirePortalAccess>
              }
            />

            {/* Time tracking */}
            <Route
              path="/timesheets"
              element={
                <RequirePortalAccess moduleCode="TIMESHEETS">
                  <RequirePasswordChangeClear>
                    <Timesheets />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/timesheets/:timesheetId"
              element={
                <RequirePortalAccess moduleCode="TIMESHEETS">
                  <RequirePasswordChangeClear>
                    <TimesheetDetail />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/employees/:employeeId/tasks"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <EmployeeTasks />
                </RequireSelfOrManager>
              }
            />
            <Route
              path="/employees/:employeeId/projects"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <EmployeeProjects />
                </RequireSelfOrManager>
              }
            />
            <Route
              path="/manager/timelogs"
              element={
                <RequirePortalAccess moduleCode="TIMESHEETS">
                  <RequirePasswordChangeClear>
                    <TimeLogsPage />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/timelogs"
              element={
                <RequirePortalAccess moduleCode="TIMESHEETS">
                  <RequirePasswordChangeClear>
                    <TimeLogsPage />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/worklogs"
              element={
                <RequirePortalAccess moduleCode="WORKLOG">
                  <RequirePasswordChangeClear>
                    <AdminAllWorkLogs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/worklogs/bulk-upload"
              element={
                <RequirePortalAccess moduleCode="WORKLOG" action="import">
                  <RequirePasswordChangeClear>
                    <BulkWorkLogUpload />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/employees/:employeeId/timelogs"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <TimeLogsPage />
                </RequireSelfOrManager>
              }
            />
            <Route
              path="/employees/:employeeId/weeklylog"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <WeeklyLog />
                </RequireSelfOrManager>
              }
            />
            <Route
              path="/employees/:employeeId/work-calendar"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <WorkLogCalendarPage />
                </RequireSelfOrManager>
              }
            />

            {/* Issues */}
            <Route
              path="/manager/issues"
              element={
                <RequirePortalAccess moduleCode="BUG">
                  <RequirePasswordChangeClear>
                    <ManagerIssues />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/manager/external-bugs"
              element={
                <RequirePortalAccess moduleCode="BUG">
                  <RequirePasswordChangeClear>
                    <ExternalBugs />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/employees/:employeeId/projects"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <EmployeeProjects />
                </RequireSelfOrManager>
              }
            />
            <Route
              path="/employees/:employeeId/issues"
              element={
                <RequireSelfOrManager paramName="employeeId">
                  <EmployeeIssues />
                </RequireSelfOrManager>
              }
            />

            {/* Invoices */}
            <Route
              path="/invoices"
              element={
                <RequirePortalAccess moduleCode="REPORT">
                  <RequirePasswordChangeClear>
                    <Invoices />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />

            {/* Admin people management */}
            <Route
              path="/users"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <AdminUsers />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/allocations"
              element={
                <RequireRoles roles={["ADMIN", "PROJECT_MANAGER", "EMPLOYEE"]}>
                  <RequirePasswordChangeClear>
                    <Allocations />
                  </RequirePasswordChangeClear>
                </RequireRoles>
              }
            />
            <Route
              path="/users/allocations"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <AdminAllocations />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/users/:id"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <UserProfile />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            {/* Enhanced Employee Detail Page */}
            <Route
              path="/employees/:id/detail"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <EmployeeDetail />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            {/* Legacy /admin/users → /users redirect */}
            <Route
              path="/admin/users"
              element={<Navigate to="/users" replace />}
            />
            <Route
              path="/admin/assign-manager"
              element={
                <RequirePortalAccess moduleCode="USER" action="update">
                  <RequirePasswordChangeClear>
                    <AdminAssignManager />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/assign-employee"
              element={
                <RequirePortalAccess moduleCode="USER" action="update">
                  <RequirePasswordChangeClear>
                    <AdminAssignEmployee />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/employees"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <AdminPeople />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/managers"
              element={
                <RequirePortalAccess moduleCode="USERS">
                  <RequirePasswordChangeClear>
                    <AdminPeople />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/employees/new"
              element={
                <RequirePortalAccess moduleCode="USER" action="create">
                  <RequirePasswordChangeClear>
                    <AdminAddEmployee />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/import-export"
              element={
                <RequirePortalAccess moduleCode="MIGRATIONS">
                  <RequirePasswordChangeClear>
                    <AdminImportExport />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/admin/timesheets/import "
              element={
                <RequirePortalAccess moduleCode="TIMESHEETS" action="import">
                  <RequirePasswordChangeClear>
                    <TimesheetImportPage />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
            <Route
              path="/manager/import-export"
              element={
                <RequirePortalAccess moduleCode="MIGRATIONS">
                  <RequirePasswordChangeClear>
                    <ManagerImportExport />
                  </RequirePasswordChangeClear>
                </RequirePortalAccess>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {/* Help system components */}
      <HelpProvider />
      <CommandPalette />
      <OnboardingTour />
    </ErrorBoundary>
  );
}

export default App;
